import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { X, Calendar as CalendarIcon, CheckCircle } from "lucide-react";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  startOfWeek,
  endOfWeek,
  isSameMonth,
} from "date-fns";
import { getAttendanceRecords } from "../../services/attendance";
import { AttendanceRecord } from "../../types/attendance";
import toast from "react-hot-toast";

interface AttendanceCalendarModalProps {
  userId: string;
  userName: string;
  userType: string;
  onClose: () => void;
}

const AttendanceCalendarModal: React.FC<AttendanceCalendarModalProps> = ({
  userId,
  userName,
  userType,
  onClose,
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [attendanceRecords, setAttendanceRecords] = useState<
    AttendanceRecord[]
  >([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAttendance();
  }, [currentMonth, userId]);

  const loadAttendance = async () => {
    try {
      setLoading(true);
      const monthStart = startOfMonth(currentMonth);
      const monthEnd = endOfMonth(currentMonth);

      const response = await getAttendanceRecords({
        userId,
        dateFrom: format(monthStart, "yyyy-MM-dd"),
        dateTo: format(monthEnd, "yyyy-MM-dd"),
        page: 1,
        limit: 100,
      });

      setAttendanceRecords(response.data || []);
    } catch (error) {
      toast.error("Failed to load attendance data");
      console.error("Error loading attendance:", error);
    } finally {
      setLoading(false);
    }
  };

  const previousMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1),
    );
  };

  const nextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1),
    );
  };

  const getAttendanceForDate = (date: Date) => {
    return attendanceRecords.find((record) => {
      const recordDate = new Date(record.date);
      return isSameDay(recordDate, date);
    });
  };

  // Generate calendar days
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const startDate = startOfWeek(monthStart);
  const endDate = endOfWeek(monthEnd);
  const calendarDays = eachDayOfInterval({ start: startDate, end: endDate });

  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const modal = (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{
        zIndex: 99999,
        backgroundColor: "rgba(0,0,0,0.65)",
        backdropFilter: "blur(4px)",
      }}
    >
      <div className="absolute inset-0" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 rounded-xl p-2">
              <CalendarIcon className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-bold leading-tight">
                Attendance Calendar
              </h2>
              <p className="text-blue-100 text-xs mt-0.5">
                {userName} ·{" "}
                {userType.charAt(0).toUpperCase() + userType.slice(1)}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-xl p-2 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Calendar Navigation */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-slate-50">
          <button
            onClick={previousMonth}
            className="p-2 hover:bg-slate-200 rounded-lg transition-colors text-slate-600 font-bold"
          >
            ←
          </button>
          <span className="text-sm font-bold text-slate-900">
            {format(currentMonth, "MMMM yyyy")}
          </span>
          <button
            onClick={nextMonth}
            className="p-2 hover:bg-slate-200 rounded-lg transition-colors text-slate-600 font-bold"
          >
            →
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-slate-200 border-t-blue-500"></div>
            </div>
          ) : (
            <>
              {/* Week day headers */}
              <div className="grid grid-cols-7 mb-2">
                {weekDays.map((day) => (
                  <div
                    key={day}
                    className="text-center font-bold text-slate-400 text-xs py-1"
                  >
                    {day.slice(0, 1)}
                  </div>
                ))}
              </div>

              {/* Calendar days */}
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((day, index) => {
                  const attendance = getAttendanceForDate(day);
                  const isCurrentMonth = isSameMonth(day, currentMonth);
                  const isToday = isSameDay(day, new Date());
                  const hasAttendance =
                    attendance &&
                    (attendance.status === "present" ||
                      attendance.status === "late");

                  return (
                    <div
                      key={index}
                      className={`
                        aspect-square rounded-lg flex flex-col items-center justify-center text-xs font-semibold transition-all
                        ${!isCurrentMonth ? "opacity-25" : ""}
                        ${
                          hasAttendance
                            ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                            : isToday
                              ? "ring-2 ring-blue-500 text-blue-600 bg-blue-50"
                              : isCurrentMonth
                                ? "text-slate-700 hover:bg-slate-50"
                                : "text-slate-400"
                        }
                      `}
                    >
                      {format(day, "d")}
                      {hasAttendance && (
                        <CheckCircle className="h-2.5 w-2.5 text-emerald-600 mt-0.5" />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Summary Stats */}
              <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 gap-3">
                <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100 text-center">
                  <p className="text-2xl font-bold text-emerald-700">
                    {
                      attendanceRecords.filter(
                        (r) => r.status === "present" || r.status === "late",
                      ).length
                    }
                  </p>
                  <p className="text-xs text-emerald-600 font-semibold mt-0.5">
                    Days Present
                  </p>
                </div>
                <div className="bg-blue-50 rounded-xl p-3 border border-blue-100 text-center">
                  <p className="text-2xl font-bold text-blue-700">
                    {attendanceRecords.length > 0
                      ? (
                          (attendanceRecords.filter(
                            (r) =>
                              r.status === "present" || r.status === "late",
                          ).length /
                            attendanceRecords.length) *
                          100
                        ).toFixed(0)
                      : 0}
                    %
                  </p>
                  <p className="text-xs text-blue-600 font-semibold mt-0.5">
                    Attendance Rate
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 pb-4">
          <button
            onClick={onClose}
            className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-xl transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default AttendanceCalendarModal;
